import { useQuery, useQueries } from 'react-query';
import useAbortController from '../useAbortController';
import NaurService from '../../services/naurService';
import { useMemo } from 'react';

/**
 * Custom hook to fetch NAUR events for a specific unit
 * Uses React Query for caching and state management (5-minute cache)
 *
 * @param {string} unitId - The unit ID to fetch NAUR events for
 * @returns React Query result with naurEvents data
 */
export const useNaurEventsForUnit = (unitId: ?string) => {
  const naurService = useMemo(() => new NaurService(), []);
  const abortControllerOptions = useAbortController();

  return useQuery(
    ['naurEventsForUnit', unitId],
    async () => {
      if (!unitId) {
        throw new Error('Unit ID is required');
      }
      return await naurService.getNaurEventsForUnit(
        unitId,
        abortControllerOptions,
      );
    },
    {
      enabled: !!unitId, // Only fetch if unitId exists
    },
  );
};

/**
 * Custom hook to check if a unit is impacted by NAUR
 * Uses React Query for caching with conditional fetching
 *
 * @param {string} unitId - The unit ID to check
 * @param {boolean} shouldFetch - Whether to make the API call (conditional fetching)
 * @returns React Query result with NAUR warning data
 */
export const useUnitNaurWarning = (
  unitId: ?string,
  shouldFetch: boolean = true,
) => {
  const naurService = useMemo(() => new NaurService(), []);
  const abortControllerOptions = useAbortController();

  return useQuery(
    ['unitNaurWarning', unitId],
    async () => {
      if (!unitId) {
        throw new Error('Unit ID is required');
      }
      return await naurService.checkUnitImpact(unitId, abortControllerOptions);
    },
    {
      enabled: !!unitId && shouldFetch, // Only fetch if conditions are met
    },
  );
};

/**
 * Custom hook to check if a unit is subject to a move-in date restriction
 * due to a PENDING_MOVE_IN NAUR event in its building.
 *
 * - Only fetches when `enabled` is true (caller must gate on the `naur` flag
 *   and on the unit NOT being an affordable/LIHTC unit).
 * - Fails silently on network/5xx errors so the user is never blocked by a
 *   warning-check failure.
 *
 * @param {string | null} unitId - The unit ID to check
 * @param {boolean} enabled - Whether to make the API call
 * @returns React Query result with pending move-in warning data
 */
export const useUnitPendingMoveInWarning = (
  unitId: ?string,
  enabled: boolean = true,
) => {
  const naurService = useMemo(() => new NaurService(), []);
  const abortControllerOptions = useAbortController();

  return useQuery(
    ['unitPendingMoveInWarning', unitId],
    async () => {
      if (!unitId) {
        throw new Error('Unit ID is required');
      }
      return await naurService.checkUnitPendingMoveInImpact(
        unitId,
        abortControllerOptions,
      );
    },
    {
      enabled: !!unitId && enabled,
      retry: false, // Fail silently — do not retry on error (UX req #4)
    },
  );
};

/**
 * Custom hook to fetch NAUR warning data for multiple units in parallel
 * Uses React Query's useQueries for efficient batch fetching
 *
 * @param {Array<Object>} units - Array of unit objects with { unitId, isFloorPlanAffordable }
 * @param {boolean} enabled - Whether to enable fetching (feature flag check)
 * @returns {Object} Map of unitId -> NAUR warning data
 */
export const useMarketRateUnitsNaurWarnings = (
  units: Array<{ unitId: ?string, isFloorPlanAffordable: boolean }>,
  enabled: boolean = true,
) => {
  const naurService = useMemo(() => new NaurService(), []);
  const abortControllerOptions = useAbortController();

  const marketRateUnits = (units || []).filter(
    (unit) => !unit.isFloorPlanAffordable && unit.unitId,
  );

  const queries = useQueries(
    marketRateUnits.map((unit) => ({
      queryKey: ['unitNaurWarning', unit.unitId],
      queryFn: async () => {
        return await naurService.checkUnitImpact(
          unit.unitId,
          abortControllerOptions,
        );
      },
      enabled: enabled,
      staleTime: 5 * 60 * 1000, // 5 minutes - good cache
      cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    })),
  );

  // Convert array of query results to map of unitId -> data
  // Memoize to prevent unnecessary re-renders when data hasn't changed
  return useMemo(() => {
    return marketRateUnits.reduce((acc, unit, index) => {
      acc[unit.unitId] = queries[index]?.data || null;
      return acc;
    }, {});
  }, [marketRateUnits, queries]);
};
